import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DashboardDataTable from 'modules/common/dashboardDataTable.component';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

const useStyles = makeStyles((theme) => ({
  filtersContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  formControl: {
    minWidth: 200,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  warningRow: {
    backgroundColor: `${theme.palette.warning.light}30`, // Yellow highlight for > 14 days
  },
  urgentRow: {
    backgroundColor: `${theme.palette.error.light}30`, // Red highlight for > 30 days
  },
  noVouchers: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  daysCell: {
    fontWeight: 'bold',
  },
  clickableLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  descriptionCell: {
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const VoucherFollowUpGrid = ({ vouchers, isLoading, showExpenseAnalyst }) => {
  const classes = useStyles();

  const [filters, setFilters] = useState({
    statusName: null,
    daysRange: null,
  });

  const formatCurrency = (amount) => {
    if (amount == null) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getRowClassName = (daysSinceEntry) => {
    if (!daysSinceEntry) return '';
    if (daysSinceEntry > 30) return classes.urgentRow;
    if (daysSinceEntry > 14) return classes.warningRow;
    return '';
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      statusName: null,
      daysRange: null,
    });
  };

  // Extract unique statuses from vouchers
  const statuses = useMemo(() => {
    const uniqueStatuses = [...new Set(vouchers.map((voucher) => voucher.statusName))].filter(Boolean);
    return uniqueStatuses.sort();
  }, [vouchers]);

  // Filter vouchers (sorting is handled by the grid)
  const filteredVouchers = useMemo(() => {
    let filtered = [...vouchers];

    if (filters.statusName) {
      filtered = filtered.filter((voucher) => voucher.statusName === filters.statusName);
    }

    if (filters.daysRange) {
      const threshold = parseInt(filters.daysRange, 10);
      filtered = filtered.filter((voucher) => voucher.daysSinceEntry > threshold);
    }

    return filtered;
  }, [vouchers, filters]);

  const columns = [
    {
      accessorKey: 'authId',
      header: 'Auth ID',
      Cell: ({ row }) => (
        <a
          href={`/authorizations/${row.original.fileId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.clickableLink}
        >
          {row.original.authId || '-'}
        </a>
      ),
    },
    {
      accessorKey: 'eeName',
      header: 'EE Name',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    // Only shown on the expense manager dashboard; EA dashboard rows have no analyst values
    ...(showExpenseAnalyst
      ? [
        {
          accessorKey: 'expenseAnalystName',
          header: 'Expense Analyst',
          Cell: ({ cell }) => cell.getValue() || '-',
        },
      ]
      : []),
    {
      accessorKey: 'clientName',
      header: 'Client Name',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'payTo',
      header: 'Pay To',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'description',
      header: 'Description',
      enableSorting: false,
      muiTableBodyCellProps: { className: classes.descriptionCell },
      Cell: ({ row }) => (
        <Tooltip title={row.original.description || ''} arrow>
          <a
            href={`/vouchers/${row.original.voucherId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.clickableLink}
          >
            {truncateText(row.original.description, 50)}
          </a>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      Cell: ({ cell }) => formatCurrency(cell.getValue()),
    },
    {
      accessorKey: 'daysSinceEntry',
      header: 'Days Since Entry',
      muiTableBodyCellProps: { className: classes.daysCell },
      Cell: ({ cell }) => (cell.getValue() != null ? `${cell.getValue()}` : '-'),
    },
    {
      accessorKey: 'statusName',
      header: 'Status',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
  ];

  if (isLoading) {
    return (
      <Paper elevation={0} className={classes.tableContainer}>
        <Typography className={classes.noVouchers} color="textSecondary">
          Loading vouchers...
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      {/* Filter UI */}
      <Box className={classes.filtersContainer}>
        {/* Status Filter */}
        <FormControl className={classes.formControl} size="small" disabled={isLoading}>
          <InputLabel id="voucher-status-filter-label">Status</InputLabel>
          <Select
            labelId="voucher-status-filter-label"
            id="voucher-status-filter"
            value={filters.statusName || ''}
            onChange={(e) => handleFilterChange('statusName', e.target.value || null)}
          >
            <MenuItem value="">
              <em>All Statuses</em>
            </MenuItem>
            {statuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Days Since Entry Range Filter */}
        <FormControl className={classes.formControl} size="small" disabled={isLoading}>
          <InputLabel id="voucher-days-filter-label">Days Since Entry</InputLabel>
          <Select
            labelId="voucher-days-filter-label"
            id="voucher-days-filter"
            value={filters.daysRange || ''}
            onChange={(e) => handleFilterChange('daysRange', e.target.value || null)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="7">More than 7 days</MenuItem>
            <MenuItem value="14">More than 14 days</MenuItem>
            <MenuItem value="30">More than 30 days</MenuItem>
          </Select>
        </FormControl>

        {/* Clear Filters Button */}
        <Button variant="outlined" size="small" onClick={handleClearFilters} disabled={isLoading}>
          Clear Filters
        </Button>
      </Box>

      {/* Grid */}
      {vouchers.length === 0 ? (
        <Paper elevation={0} className={classes.tableContainer}>
          <Typography className={classes.noVouchers} color="textSecondary">
            No vouchers require follow-up
          </Typography>
        </Paper>
      ) : filteredVouchers.length === 0 ? (
        <Paper elevation={0} className={classes.tableContainer}>
          <Typography className={classes.noVouchers} color="textSecondary">
            No vouchers match the current filters
          </Typography>
        </Paper>
      ) : (
        <Box className={classes.tableContainer}>
          <DashboardDataTable
            columns={columns}
            data={filteredVouchers}
            initialSorting={[{ id: 'daysSinceEntry', desc: true }]}
            muiTableBodyRowProps={({ row }) => {
              const rowClassName = getRowClassName(row.original.daysSinceEntry);
              return rowClassName ? { className: rowClassName } : {};
            }}
          />
        </Box>
      )}
    </>
  );
};

VoucherFollowUpGrid.propTypes = {
  vouchers: PropTypes.arrayOf(
    PropTypes.shape({
      voucherId: PropTypes.number.isRequired,
      fileId: PropTypes.number.isRequired,
      authId: PropTypes.string,
      eeName: PropTypes.string,
      clientName: PropTypes.string,
      payTo: PropTypes.string,
      description: PropTypes.string,
      amount: PropTypes.number,
      entryDate: PropTypes.string,
      daysSinceEntry: PropTypes.number,
      statusName: PropTypes.string,
    }),
  ).isRequired,
  isLoading: PropTypes.bool,
  showExpenseAnalyst: PropTypes.bool,
};

VoucherFollowUpGrid.defaultProps = {
  isLoading: false,
  showExpenseAnalyst: false,
};

export default VoucherFollowUpGrid;
